import { decimal, label, projectDate, timezoneOffset } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Notice, Row, Small } from '../styles';
import { choice, rdCatalogue, textField } from '../types';

export interface ProjectSettingsCardProps {
  workspace: KspWorkspace;
  projectId: string;
  timezone: string;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
}

const POLICY: Record<string, string> = {
  field: 'Прораб',
  pto: 'ПТО',
  explicit_split: 'Явное разделение',
};

/** Настройки проекта КСП и полнота источников — раздел «Данные проекта». */
export function ProjectSettingsCard({
  workspace: d,
  projectId,
  timezone,
  permission,
  open,
  execute,
}: ProjectSettingsCardProps) {
  const rdCodes = d.settings.expected_rd_codes ?? d.settings.settings?.expected_rd_codes ?? [];
  return (
    <Card>
      <Row>
        <h3>Настройки проекта и полнота данных</h3>
        <Button
          $variant="secondary"
          disabled={!permission('manage')}
          title={permission('manage') ? undefined : 'Менять настройки может тот, кто ведёт проект в КСП'}
          onClick={() =>
            open(
              'Настройки проекта',
              [
                textField('timezone', 'Часовой пояс IANA', true, d.settings.timezone),
                {
                  ...textField(
                    'notification_time',
                    'Время ежедневного сообщения',
                    true,
                    d.settings.daily_notice_time,
                  ),
                  type: 'time',
                },
                textField('customer_name', 'Заказчик', true, d.settings.customer),
                textField('organization_name', 'Отправитель', true, d.settings.sender),
                {
                  ...textField(
                    'expected_rd_codes',
                    'Ожидаемые шифры комплектов РД (по одному в строке)',
                    false,
                    rdCodes.join('\n'),
                  ),
                  type: 'textarea',
                  help: 'Утверждённый перечень. Пустое поле сохраняет неизвестную полноту; процент рассчитывается по документам ПТО.',
                },
                textField(
                  'expected_rd_basis',
                  'Основание ожидаемого перечня РД',
                  false,
                  d.settings.expected_rd_basis ?? d.settings.settings?.expected_rd_basis ?? '',
                ),
                choice(
                  'actual_source_policy',
                  'Источник факта по умолчанию',
                  [
                    { value: 'field', label: 'Прораб' },
                    { value: 'pto', label: 'ПТО' },
                    { value: 'explicit_split', label: 'Явное разделение' },
                  ],
                  d.settings.actual_source_policy,
                ),
              ],
              (values) =>
                execute(
                  `projects/${projectId}/settings`,
                  { ...values, ...rdCatalogue(values) },
                  'PATCH',
                ),
            )
          }
        >
          Изменить настройки
        </Button>
      </Row>
      <Muted>
        Заказчик: {d.settings.customer || '—'} · отправитель писем: {d.settings.sender || '—'} ·
        время объекта {timezoneOffset(timezone)} · ежедневное сообщение в{' '}
        {(d.settings.daily_notice_time || '—').slice(0, 5)} · факт по умолчанию:{' '}
        {d.settings.actual_source_policy
          ? (POLICY[d.settings.actual_source_policy] ?? label(d.settings.actual_source_policy))
          : 'не задан'}
      </Muted>
      {d.rd_count_coverage ? (
        <Notice>
          Подтверждённых комплектов РД: {d.rd_count_coverage.received_count}
          {d.rd_count_coverage.expected_count != null
            ? ` из ${d.rd_count_coverage.expected_count} · полнота ${decimal(d.rd_count_coverage.coverage_pct, ' %')}.`
            : ' · перечень ожидаемых комплектов не задан, полнота не оценивается.'}
          {d.rd_count_coverage.missing_codes.length > 0 &&
            ` Не получены: ${d.rd_count_coverage.missing_codes.join(', ')}.`}
        </Notice>
      ) : (
        rdCodes.length === 0 && (
          <Notice>
            Перечень ожидаемых комплектов РД не задан — полнота РД не оценивается. Укажите шифры в
            настройках.
          </Notice>
        )
      )}
      {d.source_health?.map((source) => (
        <Muted key={source.source}>
          {label(source.source)}: {label(source.status)} ·{' '}
          {source.as_of ? `обновлено ${projectDate(source.as_of, timezone)}` : 'обновление неизвестно'}
          {source.reason ? ` · ${source.reason}` : ''}
        </Muted>
      ))}
      <Small>Предупреждение о просрочке вехи начинается за 14 календарных дней до договорной даты.</Small>
    </Card>
  );
}
